import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Lock, Sparkles, History, Pencil, Copy, Library, Trash2, Archive, CalendarDays, Check, X, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Section, Empty, StatusBadge } from "@/components/kit";
import SecureImage from "@/components/SecureImage";
import useLoad from "@/lib/useLoad";
import api, { errMsg, fmtDate } from "@/lib/api";

function dateWindow(startDate) {
  const from = new Date(`${startDate}T12:00:00`);
  const to = new Date(from);
  to.setDate(to.getDate() + 6);
  return { from, to };
}

function planStartDateLabel(dateString) {
  return new Intl.DateTimeFormat("en-US", { weekday: "short", month: "short", day: "numeric", year: "numeric" }).format(new Date(`${dateString}T12:00:00`));
}

export function NotesTab({ client, reloadClient }) {
  const [f, setF] = useState({ goal: client.goal || "", health_notes: client.health_notes || "" });
  const save = async () => {
    try { await api.put(`/clients/${client.id}`, f); toast.success("Client updated"); reloadClient(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="max-w-2xl">
      <Section title="Notes">
        <div className="space-y-3">
          <div><Label>Goal</Label><Input value={f.goal} onChange={(e) => setF({ ...f, goal: e.target.value })} className="mt-1" data-testid="client-goal-input" /></div>
          <div><Label className="flex items-center gap-1"><Lock size={12} className="text-amber-400" />Sensitive health notes</Label>
            <Textarea rows={5} value={f.health_notes} onChange={(e) => setF({ ...f, health_notes: e.target.value })} className="mt-1" data-testid="client-health-notes-input" />
            <p className="text-xs text-slate-500 mt-1">Visible only to coaches and the member; every view and edit is audit-logged. Used as AI context.</p></div>
          <Button onClick={save} data-testid="client-save-btn">Save profile</Button>
        </div>
      </Section>
    </div>
  );
}

export function LocationTab({ client, reloadClient }) {
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [geoData, setGeoData] = useState(null);
  const [geoLoadError, setGeoLoadError] = useState(false);
  const [location, setLocation] = useState({ country: client.country || "", state: client.state || "", city: client.city || "" });
  useEffect(() => {
    if (!editing || geoData) return undefined;
    let active = true;
    Promise.all([import("country-state-city"), import("tz-lookup")])
      .then(([geography, timezoneModule]) => {
        if (active) setGeoData({ ...geography, timezoneLookup: timezoneModule.default || timezoneModule });
      })
      .catch(() => { if (active) setGeoLoadError(true); });
    return () => { active = false; };
  }, [editing, geoData]);

  const COUNTRIES = geoData?.Country.getAllCountries() || [];
  const countryCode = COUNTRIES.find((country) => country.name === location.country)?.isoCode || "";
  const states = countryCode ? geoData?.State.getStatesOfCountry(countryCode) || [] : [];
  const stateCode = states.find((state) => state.name === location.state)?.isoCode || "";
  const cities = countryCode && stateCode ? geoData?.City.getCitiesOfState(countryCode, stateCode) || [] : [];
  const cityIndex = cities.findIndex((city) => city.name === location.city);

  const save = async () => {
    setSaving(true);
    try {
      const selectedCity = cityIndex >= 0 ? cities[cityIndex] : null;
      const update = { ...location };
      if (selectedCity) {
        update.timezone = geoData.timezoneLookup(Number(selectedCity.latitude), Number(selectedCity.longitude));
      }
      const { data } = await api.put(`/clients/${client.id}`, update);
      setLocation({ country: data.country || "", state: data.state || "", city: data.city || "" });
      setEditing(false);
      reloadClient();
      toast.success(selectedCity ? `Location and timezone updated to ${update.timezone}` : "Location updated");
    } catch (error) {
      toast.error(errMsg(error));
    } finally {
      setSaving(false);
    }
  };

  const cancel = () => {
    setLocation({ country: client.country || "", state: client.state || "", city: client.city || "" });
    setEditing(false);
  };

  return (
    <div className="max-w-2xl">
      <Section title="Location" action={editing ? (
        <div className="flex gap-2">
          <Button type="button" variant="outline" size="sm" disabled={saving} onClick={cancel}><X size={14} className="mr-1" />Cancel</Button>
          <Button type="button" size="sm" disabled={saving} onClick={save}><Check size={14} className="mr-1" />{saving ? "Saving…" : "Save"}</Button>
        </div>
      ) : <Button type="button" variant="outline" size="sm" onClick={() => setEditing(true)}><Pencil size={14} className="mr-1" />Edit</Button>}>
        {editing ? (
          !geoData ? (
            <div className="text-sm text-slate-400">{geoLoadError ? "Location options could not be loaded. Close and reopen Edit to retry." : "Loading location options…"}</div>
          ) : <div className="space-y-3">
            <div><Label>Country</Label>
              <Select value={countryCode} onValueChange={(value) => {
                const country = COUNTRIES.find((item) => item.isoCode === value);
                setLocation({ country: country?.name || "", state: "", city: "" });
              }}>
                <SelectTrigger className="mt-1" data-testid="client-location-country"><SelectValue placeholder="Select country" /></SelectTrigger>
                <SelectContent className="max-h-72">{COUNTRIES.map((country) => <SelectItem key={country.isoCode} value={country.isoCode}>{country.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>State</Label>
              <Select value={stateCode} disabled={!countryCode} onValueChange={(value) => {
                const state = states.find((item) => item.isoCode === value);
                setLocation({ ...location, state: state?.name || "", city: "" });
              }}>
                <SelectTrigger className="mt-1" data-testid="client-location-state"><SelectValue placeholder={countryCode ? "Select state" : "Select a country first"} /></SelectTrigger>
                <SelectContent className="max-h-72">{states.map((state) => <SelectItem key={state.isoCode} value={state.isoCode}>{state.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>City</Label>
              <Select value={cityIndex >= 0 ? String(cityIndex) : ""} disabled={!stateCode} onValueChange={(value) => setLocation({ ...location, city: cities[Number(value)]?.name || "" })}>
                <SelectTrigger className="mt-1" data-testid="client-location-city"><SelectValue placeholder={stateCode ? "Select city" : "Select a state first"} /></SelectTrigger>
                <SelectContent className="max-h-72">{cities.map((city, index) => <SelectItem key={`${city.name}-${index}`} value={String(index)}>{city.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
        ) : (
          <div className="grid sm:grid-cols-3 gap-4 text-sm">
            <div><div className="eyebrow">Country</div><div className="mt-1">{location.country || "-"}</div></div>
            <div><div className="eyebrow">State</div><div className="mt-1">{location.state || "-"}</div></div>
            <div><div className="eyebrow">City</div><div className="mt-1">{location.city || "-"}</div></div>
          </div>
        )}
      </Section>
    </div>
  );
}

function ClientPlanTile({ plan, onRename, onDuplicateMember, onDuplicateLibrary, onDelete, onArchive, onSchedule, occupiedRanges, earliestDate }) {
  const [editing, setEditing] = useState(false);
  const [title, setTitle] = useState(plan.title);
  const [saving, setSaving] = useState(false);
  const [datePickerOpen, setDatePickerOpen] = useState(false);
  const [scheduling, setScheduling] = useState(false);
  const titleEditorRef = useRef(null);
  const days = plan.content?.days || [];
  const dayCount = plan.type === "workout"
    ? days.filter((day) => (day.exercises || []).some((exercise) => String(exercise.name || "").trim())).length
    : days.length;

  const saveTitle = async (event) => {
    event.preventDefault();
    const nextTitle = title.trim();
    if (!nextTitle) return toast.error("Plan name is required");
    if (nextTitle === plan.title) return setEditing(false);
    setSaving(true);
    const saved = await onRename(plan, nextTitle);
    setSaving(false);
    if (saved) setEditing(false);
  };

  useEffect(() => {
    if (!editing) return undefined;
    const cancelOutside = (event) => {
      if (!saving && !titleEditorRef.current?.contains(event.target)) {
        setTitle(plan.title);
        setEditing(false);
      }
    };
    document.addEventListener("pointerdown", cancelOutside);
    return () => document.removeEventListener("pointerdown", cancelOutside);
  }, [editing, plan.title, saving]);

  return (
    <div className="panel rounded-md px-3 py-2 flex min-w-0 items-center gap-2" data-testid={`client-plan-${plan.id}`}>
      <div className="flex min-w-0 flex-1 items-center gap-1">
        {editing ? (
          <form ref={titleEditorRef} onSubmit={saveTitle} className="flex min-w-0 flex-1 items-center gap-1">
            <Input autoFocus value={title} onChange={(event) => setTitle(event.target.value)} className="h-9 min-w-0 flex-1 bg-[#111827] font-mono-k text-sm uppercase tracking-wider" aria-label="Plan name" data-testid={`client-plan-title-${plan.id}`} />
            <Button type="submit" variant="ghost" size="icon" className="h-7 w-7 shrink-0" disabled={saving} aria-label="Save plan name" title="Save plan name"><Check size={14} /></Button>
            <Button type="button" variant="ghost" size="icon" className="h-7 w-7 shrink-0" disabled={saving} onClick={() => { setTitle(plan.title); setEditing(false); }} aria-label="Cancel rename" title="Cancel rename"><X size={14} /></Button>
          </form>
        ) : (
          <Link to={`/coach/plans/${plan.id}`} className="w-fit max-w-[40%] truncate whitespace-nowrap font-mono-k text-sm uppercase tracking-wider hover:text-[#FF3B30]" data-testid={`client-plan-title-link-${plan.id}`}>{plan.title}</Link>
        )}
        <div className="flex shrink-0 items-center gap-0.5">
          {!editing && <Button type="button" variant="ghost" size="icon" className="h-7 w-7" onClick={() => setEditing(true)} aria-label="Edit plan name" title="Edit plan name" data-testid={`client-plan-rename-${plan.id}`}><Pencil size={14} /></Button>}
          {plan.type === "workout" ? (
            <>
              <Button type="button" variant="ghost" size="icon" className="h-7 w-7" onClick={() => onDuplicateMember(plan)} aria-label="Copy workout to this member" title="Copy to this member as draft" data-testid={`client-plan-copy-member-${plan.id}`}><Copy size={14} /></Button>
              <Button type="button" variant="ghost" size="icon" className="h-7 w-7" onClick={() => onDuplicateLibrary(plan)} aria-label="Copy workout to template library" title="Copy to template library" data-testid={`client-plan-copy-template-${plan.id}`}><Library size={14} /></Button>
            </>
          ) : (
            <Button type="button" variant="ghost" size="icon" className="h-7 w-7" onClick={() => onDuplicateLibrary(plan)} aria-label="Copy meal plan to template library" title="Copy to template library" data-testid={`client-plan-duplicate-${plan.id}`}><Copy size={14} /></Button>
          )}
          <Button type="button" variant="ghost" size="icon" className="h-7 w-7 text-red-400" onClick={() => onDelete(plan)} aria-label="Delete plan" title="Delete plan" data-testid={`client-plan-delete-${plan.id}`}><Trash2 size={14} /></Button>
        </div>
      </div>
      <div className="w-24 shrink-0 whitespace-nowrap text-xs text-slate-500">{plan.type === "workout" ? `${dayCount} days/week` : `${dayCount} day(s)`}</div>
      <div className="w-40 shrink-0 whitespace-nowrap text-xs text-slate-500">
        {plan.type === "workout" && plan.status === "draft" && !plan.start_date ? (
          <Popover open={datePickerOpen} onOpenChange={setDatePickerOpen}>
            <PopoverTrigger asChild>
              <Button type="button" variant="outline" size="sm" className="h-7 w-32 justify-start px-2 text-xs font-normal" disabled={scheduling} data-testid={`client-plan-start-date-${plan.id}`}>
                <CalendarDays size={14} className="mr-1 shrink-0" />Choose date
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar
                mode="single"
                weekStartsOn={1}
                disabled={[{ before: earliestDate }, ...occupiedRanges]}
                modifiers={{ recommendedMonday: { dayOfWeek: [1] }, occupied: occupiedRanges }}
                modifiersClassNames={{
                  recommendedMonday: "rounded-md bg-emerald-500/20 font-bold text-emerald-300",
                  occupied: "rounded-md bg-amber-500/25 font-semibold text-amber-200",
                }}
                onSelect={async (date) => {
                  if (!date) return;
                  const startDate = [date.getFullYear(), String(date.getMonth() + 1).padStart(2, "0"), String(date.getDate()).padStart(2, "0")].join("-");
                  setScheduling(true);
                  const scheduled = await onSchedule(plan, startDate);
                  setScheduling(false);
                  if (scheduled) setDatePickerOpen(false);
                }}
                initialFocus
              />
              <p className="px-3 pb-3 text-xs text-slate-400"><span className="text-emerald-300">Monday</span> is recommended. <span className="text-amber-200">Amber</span> dates are occupied.</p>
            </PopoverContent>
          </Popover>
        ) : plan.start_date ? planStartDateLabel(plan.start_date) : "—"}
      </div>
      <div className="w-40 shrink-0 whitespace-nowrap text-xs text-slate-500">
        {plan.type === "workout" && plan.status !== "archived" ? (
          <Button type="button" variant="outline" size="sm" className="h-7 px-2 text-xs" onClick={() => onArchive(plan)} aria-label={`Archive ${plan.title}`} title="Archive workout plan" data-testid={`client-plan-archive-${plan.id}`}>
            <Archive size={13} className="mr-1" />Archive
          </Button>
        ) : plan.end_date ? planStartDateLabel(plan.end_date) : "—"}
      </div>
      <div className="w-20 shrink-0 text-right"><StatusBadge status={plan.status} /></div>
    </div>
  );
}

export function PlansTab({ client }) {
  const nav = useNavigate();
  const [plans, reload] = useLoad("/plans", { client_id: client.id });
  const [templates] = useLoad("/plans", { kind: "templates" });
  const [selectedWorkoutId, setSelectedWorkoutId] = useState("");
  const [selectedMealId, setSelectedMealId] = useState("");
  const [scheduleQueue, setScheduleQueue] = useState([]);
  const [openDatePicker, setOpenDatePicker] = useState(null);
  const workoutPlans = (templates || []).filter((plan) => plan.type === "workout");
  const mealTemplates = (templates || []).filter((plan) => plan.type === "meal");
  const selectedWorkout = workoutPlans.find((plan) => plan.id === selectedWorkoutId);
  const occupiedRangesFor = (planId) => [
    ...(plans || [])
      .filter((plan) => plan.type === "workout" && ["active", "scheduled"].includes(plan.status) && plan.start_date)
      .map((plan) => dateWindow(plan.start_date)),
    ...scheduleQueue
      .filter((item) => item.plan_id !== planId && item.start_date)
      .map((item) => dateWindow(item.start_date)),
  ];
  const today = new Date().toISOString().slice(0, 10);
  const earliestDate = new Date(`${today}T00:00:00`);
  const assignWorkout = async () => {
    if (!selectedWorkout || selectedWorkout.status !== "template") return;
    try { await api.post(`/plans/${selectedWorkout.id}/assign`, { client_id: client.id }); toast.success("Workout plan assigned to client"); setSelectedWorkoutId(""); reload(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const addScheduledTemplate = () => {
    const plan = selectedWorkout;
    if (!plan || plan.type !== "workout" || scheduleQueue.some((item) => item.plan_id === plan.id)) return;
    setScheduleQueue([...scheduleQueue, { plan_id: plan.id, title: plan.title, start_date: "" }]);
    setSelectedWorkoutId("");
  };
  const assignMeal = async () => {
    if (!selectedMealId) return;
    try { await api.post(`/plans/${selectedMealId}/assign`, { client_id: client.id }); toast.success("Meal plan assigned to client"); setSelectedMealId(""); reload(); }
    catch (error) { toast.error(errMsg(error)); }
  };
  const schedule = async () => {
    if (scheduleQueue.some((item) => !item.start_date)) return toast.error("Choose a start date for every workout plan");
    if (new Set(scheduleQueue.map((item) => item.start_date)).size !== scheduleQueue.length) return toast.error("Each workout plan needs a different start date");
    try {
      await api.post("/plans/schedule", { client_id: client.id, assignments: scheduleQueue.map(({ plan_id, start_date }) => ({ plan_id, start_date })) });
      toast.success(`${scheduleQueue.length} workout plan(s) scheduled`);
      setScheduleQueue([]);
      reload();
    } catch (error) { toast.error(errMsg(error)); }
  };
  const rename = async (plan, title) => {
    try { await api.put(`/plans/${plan.id}`, { title }); toast.success("Plan renamed"); reload(); return true; }
    catch (error) { toast.error(errMsg(error)); return false; }
  };
  const duplicateForMember = async (plan) => {
    try { await api.post(`/plans/${plan.id}/duplicate`, { as_template: false }); toast.success("Workout copied to this member as a draft"); reload(); }
    catch (error) { toast.error(errMsg(error)); }
  };
  const duplicateToLibrary = async (plan) => {
    try { await api.post(`/plans/${plan.id}/duplicate`, { as_template: true }); toast.success("Plan copied to template library"); }
    catch (error) { toast.error(errMsg(error)); }
  };
  const remove = async (plan) => {
    if (!window.confirm(`Delete "${plan.title}"? This cannot be undone.`)) return;
    try { await api.delete(`/plans/${plan.id}`); toast.success("Plan deleted"); reload(); }
    catch (error) { toast.error(errMsg(error)); }
  };
  const archive = async (plan) => {
    if (!window.confirm(`Archive "${plan.title}"?`)) return;
    try { await api.post(`/plans/${plan.id}/archive`); toast.success("Workout plan archived"); reload(); }
    catch (error) { toast.error(errMsg(error)); }
  };
  const scheduleDraft = async (plan, start_date) => {
    try {
      await api.post("/plans/schedule", { client_id: client.id, assignments: [{ plan_id: plan.id, start_date }] });
      toast.success("Workout plan scheduled");
      reload();
      return true;
    } catch (error) {
      toast.error(errMsg(error));
      return false;
    }
  };
  const clientPlans = [
    ...(plans || []).filter((plan) => plan.type === "workout").sort((first, second) => (second.start_date || "").localeCompare(first.start_date || "")),
    ...(plans || []).filter((plan) => plan.type !== "workout"),
  ];
  return (
    <div className="space-y-6">
      <Section>
        <div className="grid gap-5 lg:grid-cols-2">
          <div className="min-w-0">
            <div className="flex flex-wrap items-end gap-2">
              <div className="w-full max-w-xs">
                <Label>Workout template</Label>
                <Select value={selectedWorkoutId} onValueChange={setSelectedWorkoutId}>
                  <SelectTrigger className="mt-1 font-mono-k text-sm uppercase tracking-wider" data-testid="member-workout-select"><SelectValue className="font-mono-k text-sm uppercase tracking-wider" placeholder="Choose a workout template" /></SelectTrigger>
                  <SelectContent>{workoutPlans.map((plan) => <SelectItem key={plan.id} value={plan.id} className="font-mono-k text-sm uppercase tracking-wider">{plan.title}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <Button type="button" variant="outline" onClick={addScheduledTemplate} disabled={!selectedWorkout || scheduleQueue.some((item) => item.plan_id === selectedWorkoutId)} data-testid="schedule-template-add">Add to schedule</Button>
              <Button type="button" onClick={assignWorkout} disabled={selectedWorkout?.status !== "template"} data-testid="assign-workout-template-btn">Assign now</Button>
              <Button variant="ghost" onClick={() => nav(`/coach/ai?client=${client.id}`)} data-testid="client-ai-draft-btn"><Sparkles size={14} className="mr-1" />AI draft for {client.name.split(" ")[0]}</Button>
            </div>
            {scheduleQueue.length > 0 && (
              <div className="mt-4 space-y-2">
                {scheduleQueue.map((item) => {
                  const occupiedRanges = occupiedRangesFor(item.plan_id);
                  const selectedWeek = item.start_date ? [dateWindow(item.start_date)] : [];
                  return <div key={item.plan_id} className="flex flex-wrap items-center gap-3 border-t border-[#1E293B] pt-3">
                    <span className="min-w-48 flex-1 font-mono-k text-sm uppercase tracking-wider">{item.title}</span>
                    <Label className="text-xs text-slate-400">Start date</Label>
                    <Popover open={openDatePicker === item.plan_id} onOpenChange={(open) => setOpenDatePicker(open ? item.plan_id : null)}>
                      <PopoverTrigger asChild>
                        <Button type="button" variant="outline" className="w-44 justify-start font-normal" data-testid={`scheduled-start-${item.plan_id}`}>
                          {item.start_date ? fmtDate(item.start_date) : "Choose date"}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <Calendar
                          mode="single"
                          weekStartsOn={1}
                          selected={item.start_date ? new Date(`${item.start_date}T12:00:00`) : undefined}
                          disabled={[{ before: earliestDate }, ...occupiedRanges]}
                          modifiers={{ recommendedMonday: { dayOfWeek: [1] }, occupied: occupiedRanges, selectedWeek }}
                          modifiersClassNames={{
                            recommendedMonday: "rounded-md bg-emerald-500/20 font-bold text-emerald-300",
                            occupied: "rounded-md bg-amber-500/25 font-semibold text-amber-200",
                            selectedWeek: "rounded-md bg-sky-500/20 text-sky-200",
                          }}
                          onSelect={(date) => {
                            if (!date) return;
                            const selectedDate = [date.getFullYear(), String(date.getMonth() + 1).padStart(2, "0"), String(date.getDate()).padStart(2, "0")].join("-");
                            setScheduleQueue(scheduleQueue.map((queued) => queued.plan_id === item.plan_id ? { ...queued, start_date: selectedDate } : queued));
                            setOpenDatePicker(null);
                          }}
                          initialFocus
                        />
                        <p className="px-3 pb-3 text-xs text-slate-400"><span className="text-emerald-300">Monday</span> is recommended. <span className="text-amber-200">Amber</span> dates are occupied.</p>
                      </PopoverContent>
                    </Popover>
                    <Button type="button" variant="ghost" size="icon" aria-label={`Remove ${item.title}`} title="Remove plan" onClick={() => setScheduleQueue(scheduleQueue.filter((queued) => queued.plan_id !== item.plan_id))}><X size={14} /></Button>
                  </div>;
                })}
                <Button type="button" onClick={schedule} disabled={scheduleQueue.some((item) => !item.start_date)} data-testid="schedule-plans-submit">Schedule {scheduleQueue.length} plan(s)</Button>
              </div>
            )}
          </div>
          <div className="min-w-0 lg:border-l lg:border-[#1E293B] lg:pl-5">
            <div className="flex flex-wrap items-end gap-2">
              <div className="w-full max-w-xs">
                <Label>Meal template</Label>
                <Select value={selectedMealId} onValueChange={setSelectedMealId}>
                  <SelectTrigger className="mt-1 font-mono-k text-sm uppercase tracking-wider" data-testid="member-meal-select"><SelectValue className="font-mono-k text-sm uppercase tracking-wider" placeholder="Choose a meal template" /></SelectTrigger>
                  <SelectContent>{mealTemplates.map((plan) => <SelectItem key={plan.id} value={plan.id} className="font-mono-k text-sm uppercase tracking-wider">{plan.title}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <Button type="button" onClick={assignMeal} disabled={!selectedMealId} data-testid="assign-meal-template-btn">Assign now</Button>
            </div>
          </div>
        </div>
      </Section>
      <Section title="Client plans" testid="client-plans-list">
        {!plans?.length ? <Empty text="No plans yet." /> : (
          <div className="overflow-x-auto">
            <div className="min-w-[48rem] space-y-2">
              <div className="hidden items-center gap-2 px-3 text-xs text-slate-400 sm:flex">
              <span className="min-w-0 flex-1">Plan</span>
              <span className="w-24 shrink-0">Details</span>
              <span className="w-40 shrink-0">Start date</span>
              <span className="w-40 shrink-0">End date</span>
              <span className="w-20 shrink-0 text-right">Status</span>
              </div>
              {clientPlans.map((plan) => <ClientPlanTile key={plan.id} plan={plan} onRename={rename} onDuplicateMember={duplicateForMember} onDuplicateLibrary={duplicateToLibrary} onDelete={remove} onArchive={archive} onSchedule={scheduleDraft} occupiedRanges={occupiedRangesFor(plan.id)} earliestDate={earliestDate} />)}
            </div>
          </div>
        )}
      </Section>
    </div>
  );
}

export function LogsTab({ client }) {
  const [logs] = useLoad("/logs", { client_id: client.id });
  const [open, setOpen] = useState(null);
  if (!logs) return null;
  if (!logs.length) return <Empty text="No workout logs yet." />;
  return (
    <div className="space-y-3" data-testid="client-logs-list">
      {logs.map((l) => (
        <Section key={l.id} title={`${l.date} · ${l.day_name || "Workout"}`} testid={`coach-log-${l.id}`}
          action={l.history?.length ? (
            <Button variant="ghost" size="sm" onClick={() => setOpen(open === l.id ? null : l.id)} data-testid={`log-history-btn-${l.id}`}>
              <History size={14} className="mr-1" />Edited {l.history.length}×
            </Button>) : null}>
          <div className="text-xs text-slate-500 mb-2">{l.duration_min ? `${l.duration_min} min · ` : ""}{l.rpe ? `RPE ${l.rpe}` : ""} {l.notes}</div>
          <div className="grid sm:grid-cols-2 gap-2">
            {l.entries.map((e, i) => (
              <div key={i} className="text-sm"><span className="font-medium">{e.exercise}</span>
                <span className="font-mono-k text-xs text-slate-400 ml-2">{e.sets.map((s) => `${s.weight ?? 0}${s.weight_unit || "kg"}×${s.reps ?? 0}`).join(", ")}</span>
                {(e.time || e.distance) && <div className="text-xs text-slate-500">{[e.time, e.distance].filter(Boolean).join(" · ")}</div>}</div>
            ))}
          </div>
          {open === l.id && (
            <div className="mt-4 border-t border-[#1E293B] pt-3 space-y-2" data-testid={`log-history-${l.id}`}>
              <div className="eyebrow">Edit history (previous versions)</div>
              {l.history.map((h, i) => (
                <div key={i} className="text-xs text-slate-400">Replaced {fmtDate(h.replaced_at)}: {h.entries.map((e) => `${e.exercise} ${e.sets.map((s) => `${s.weight ?? 0}×${s.reps ?? 0}`).join("/")}`).join("; ")}</div>
              ))}
            </div>
          )}
        </Section>
      ))}
    </div>
  );
}

function CheckinCard({ ck, onReviewed }) {
  const [notes, setNotes] = useState(ck.coach_notes || "");
  const [deleting, setDeleting] = useState(false);
  const review = async () => {
    try { await api.post(`/checkins/${ck.id}/review`, { coach_notes: notes }); toast.success("Check-in reviewed"); onReviewed(); } catch (e) { toast.error(errMsg(e)); }
  };
  const remove = async () => {
    if (!window.confirm("Delete this reviewed check-in? This cannot be undone. Progress photos will remain saved.")) return;
    setDeleting(true);
    try { await api.delete(`/checkins/${ck.id}`); toast.success("Reviewed check-in deleted"); onReviewed(); }
    catch (error) { toast.error(errMsg(error)); }
    finally { setDeleting(false); }
  };
  return (
    <Section title={fmtDate(ck.submitted_at)} testid={`coach-checkin-${ck.id}`} action={(
      <div className="flex items-center gap-2">
        <StatusBadge status={ck.reviewed ? "reviewed" : "pending"} />
        {ck.reviewed && <Button type="button" variant="ghost" size="icon" onClick={remove} disabled={deleting} aria-label="Delete reviewed check-in" title="Delete reviewed check-in" data-testid={`checkin-delete-btn-${ck.id}`}><Trash2 size={14} /></Button>}
      </div>
    )}>
      <div className="grid sm:grid-cols-3 gap-3 mb-4">
        {(ck.fields || []).map((f) => (
          <div key={f.key}><div className="eyebrow">{f.label}</div><div className="text-sm mt-1">{String(ck.answers[f.key] ?? "-")}</div></div>
        ))}
      </div>
      {ck.photo_ids?.length > 0 && (
        <div className="flex gap-2 mb-4">{ck.photo_ids.map((p) => <SecureImage key={p} photoId={p} className="w-24 h-32 object-cover rounded" />)}</div>
      )}
      <Label>Coach notes</Label>
      <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} className="mt-1" data-testid={`checkin-notes-${ck.id}`} />
      <Button className="mt-3" size="sm" onClick={review} data-testid={`checkin-review-btn-${ck.id}`}>{ck.reviewed ? "Update notes" : "Mark reviewed"}</Button>
    </Section>
  );
}

function ClientCheckinForm({ client }) {
  const [form, reload] = useLoad(`/clients/${client.id}/checkin-form`);
  const [fields, setFields] = useState([]);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (form && !Array.isArray(form)) setFields(form.additional_fields || []);
  }, [form]);
  const updateField = (index, key, value) => {
    setFields((current) => current.map((field, fieldIndex) => fieldIndex === index ? { ...field, [key]: value } : field));
  };
  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put(`/clients/${client.id}/checkin-form`, { fields });
      setFields(data.additional_fields || []);
      toast.success("Member check-in questions saved");
      reload();
    } catch (error) {
      toast.error(errMsg(error));
    } finally {
      setSaving(false);
    }
  };
  return (
    <Section title="Additional check-in questions" testid="client-checkin-form-editor">
      {!form || Array.isArray(form) ? <div className="eyebrow">Loading…</div> : (
        <>
          <div className="space-y-2">
            {fields.map((field, index) => (
              <div key={`${field.key}-${index}`} className="grid gap-2 items-center md:grid-cols-[minmax(100px,0.8fr)_minmax(150px,1.4fr)_140px_auto_auto]" data-testid={`client-checkin-field-${index}`}>
                <Input value={field.key} onChange={(event) => updateField(index, "key", event.target.value.replace(/\s+/g, "_").toLowerCase())} placeholder="key" className="font-mono-k text-xs" aria-label="Field key" />
                <Input value={field.label} onChange={(event) => updateField(index, "label", event.target.value)} placeholder="Question label" aria-label="Question label" />
                <Select value={field.type} onValueChange={(value) => updateField(index, "type", value)}>
                  <SelectTrigger aria-label="Question type"><SelectValue /></SelectTrigger>
                  <SelectContent>{["number", "scale", "text", "boolean"].map((type) => <SelectItem key={type} value={type}>{type}</SelectItem>)}</SelectContent>
                </Select>
                <label className="flex items-center gap-2 text-xs text-slate-400"><Switch checked={!!field.required} onCheckedChange={(value) => updateField(index, "required", value)} />Required</label>
                <Button type="button" variant="ghost" size="icon" onClick={() => setFields((current) => current.filter((_, fieldIndex) => fieldIndex !== index))} aria-label={`Remove ${field.label || "question"}`}><Trash2 size={14} /></Button>
              </div>
            ))}
            {!fields.length && <div className="text-sm text-slate-500">No additional questions for this member.</div>}
          </div>
          <div className="flex gap-2 mt-4">
            <Button type="button" variant="outline" onClick={() => setFields((current) => [...current, { key: `custom_field_${Date.now()}`, label: "", type: "text", required: false }])} disabled={saving} data-testid="client-checkin-add-field-btn"><Plus size={14} className="mr-1" />Add question</Button>
            <Button type="button" onClick={save} disabled={saving || !form} data-testid="client-checkin-save-form-btn">{saving ? "Saving…" : "Save questions"}</Button>
          </div>
        </>
      )}
    </Section>
  );
}

export function CheckinsTab({ client }) {
  const [list, reload] = useLoad("/checkins", { client_id: client.id });
  return (
    <div className="space-y-4">
      <ClientCheckinForm client={client} />
      {!list ? <div className="eyebrow">Loading check-ins…</div> : list.length ? list.map((ck) => <CheckinCard key={ck.id} ck={ck} onReviewed={reload} />) : <Empty text="No check-ins submitted yet." />}
    </div>
  );
}
